/**
 * App-wide Firebase auth-state context.
 *
 * Subscribes once to `onAuthStateChanged` and exposes the current user plus an
 * `isLoaded` flag (false until Firebase has resolved the persisted session).
 * Backs the `firebaseMsalCompat` shim, the auth pages, and the NavBar.
 */

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { onAuthStateChanged, type User } from 'firebase/auth';
import { auth } from './firebase';

interface FirebaseAuthState {
  /** The signed-in Firebase user, or null when signed out. */
  user: User | null;
  /** False until Firebase resolves the persisted session on first load. */
  isLoaded: boolean;
}

const FirebaseAuthContext = createContext<FirebaseAuthState>({
  user: null,
  isLoaded: false,
});

export function FirebaseAuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(auth.currentUser);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (nextUser) => {
      setUser(nextUser);
      setIsLoaded(true);
    });
    return unsubscribe;
  }, []);

  const value = useMemo<FirebaseAuthState>(
    () => ({ user, isLoaded }),
    [user, isLoaded]
  );

  return <FirebaseAuthContext value={value}>{children}</FirebaseAuthContext>;
}

/** Read the current Firebase auth state from context. */
export function useFirebaseAuth(): FirebaseAuthState {
  return useContext(FirebaseAuthContext);
}
